/**
 * Hero del login: fotos reales de winclap.com que se animan
 * scatter → línea → círculo, y quedan orbitando suave alrededor del
 * contenido central (children). Adaptado del componente scroll-morph-hero
 * que pasó Sol: sin scroll-hijack (mala UX en un login) — la secuencia es
 * automática y el círculo rota con CSS. Flip 3D al hover intacto.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

type AnimationPhase = 'scatter' | 'line' | 'circle';

const IMG_WIDTH = 96;
const IMG_HEIGHT = 134;

const IMAGES = [
  '/login-photos/01.jpg',
  '/login-photos/02.jpg',
  '/login-photos/03.jpg',
  '/login-photos/04.webp',
  '/login-photos/05.webp',
  '/login-photos/06.webp',
  '/login-photos/07.webp',
  '/login-photos/09.jpg',
  '/login-photos/11.jpg',
  '/login-photos/12.webp',
  '/login-photos/13.webp',
  '/login-photos/14.webp',
  '/login-photos/15.jpg',
];
const TOTAL = IMAGES.length;

function FlipCard({
  src,
  target,
}: {
  src: string;
  target: { x: number; y: number; rotation: number; scale: number; opacity: number };
}) {
  return (
    <motion.div
      animate={{
        x: target.x,
        y: target.y,
        rotate: target.rotation,
        scale: target.scale,
        opacity: target.opacity,
      }}
      transition={{ type: 'spring', stiffness: 40, damping: 15 }}
      style={{
        position: 'absolute',
        width: IMG_WIDTH,
        height: IMG_HEIGHT,
        transformStyle: 'preserve-3d',
        perspective: '1000px',
      }}
      className="group cursor-pointer"
    >
      <motion.div
        className="relative h-full w-full"
        style={{ transformStyle: 'preserve-3d' }}
        transition={{ duration: 0.6, type: 'spring', stiffness: 260, damping: 20 }}
        whileHover={{ rotateY: 180 }}
      >
        {/* Frente: la foto */}
        <div
          className="absolute inset-0 h-full w-full overflow-hidden rounded-xl bg-gray-200 shadow-xl ring-1 ring-black/5"
          style={{ backfaceVisibility: 'hidden' }}
        >
          <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-black/10 transition-colors group-hover:bg-transparent" />
        </div>
        {/* Dorso */}
        <div
          className="absolute inset-0 flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-xl border border-brand-gray4 bg-brand-gray1 shadow-lg"
          style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
        >
          <span className="text-lg text-white">✦</span>
          <span className="mt-1 text-[7px] font-bold uppercase tracking-[0.2em] text-white/60">
            Winclap
          </span>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function LoginPhotoHero({ children }: { children?: React.ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<AnimationPhase>('scatter');
  const [size, setSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      for (const e of entries) setSize({ width: e.contentRect.width, height: e.contentRect.height });
    });
    observer.observe(el);
    setSize({ width: el.offsetWidth, height: el.offsetHeight });
    return () => observer.disconnect();
  }, []);

  // Secuencia automática: scatter → línea → círculo
  useEffect(() => {
    const t1 = setTimeout(() => setPhase('line'), 500);
    const t2 = setTimeout(() => setPhase('circle'), 2300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Parallax suave con el mouse
  const mouseX = useMotionValue(0);
  const smoothMouseX = useSpring(mouseX, { stiffness: 30, damping: 20 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      mouseX.set(((e.clientX - rect.left) / rect.width) * 2 - 1);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, [mouseX]);
  const [parallax, setParallax] = useState(0);
  useEffect(() => smoothMouseX.on('change', (v) => setParallax(v * 24)), [smoothMouseX]);

  const scatter = useMemo(
    () =>
      IMAGES.map(() => ({
        x: (Math.random() - 0.5) * 1500,
        y: (Math.random() - 0.5) * 1000,
        rotation: (Math.random() - 0.5) * 180,
        scale: 0.6,
        opacity: 0,
      })),
    []
  );

  const minDim = Math.min(size.width || 1200, size.height || 800);
  const radius = Math.min(minDim * 0.44, 420);

  return (
    <div ref={containerRef} className="relative h-full w-full overflow-hidden bg-[#F4F2EE]">
      {/* Fondo: glows de los colores de marca flotando muy lento */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute left-[-10%] top-[-15%] h-[55vh] w-[55vh] rounded-full opacity-40 blur-3xl"
          style={{ background: 'radial-gradient(circle, #1CC9B9 0%, transparent 70%)', animation: 'wcFloatA 26s ease-in-out infinite' }}
        />
        <div
          className="absolute right-[-12%] top-[5%] h-[60vh] w-[60vh] rounded-full opacity-35 blur-3xl"
          style={{ background: 'radial-gradient(circle, #CEABFF 0%, transparent 70%)', animation: 'wcFloatB 32s ease-in-out infinite' }}
        />
        <div
          className="absolute bottom-[-20%] left-[15%] h-[55vh] w-[55vh] rounded-full opacity-35 blur-3xl"
          style={{ background: 'radial-gradient(circle, #87DBFF 0%, transparent 70%)', animation: 'wcFloatB 38s ease-in-out infinite reverse' }}
        />
        <div
          className="absolute bottom-[-10%] right-[-8%] h-[45vh] w-[45vh] rounded-full opacity-25 blur-3xl"
          style={{ background: 'radial-gradient(circle, #FE805B 0%, transparent 70%)', animation: 'wcFloatA 30s ease-in-out infinite reverse' }}
        />
        {/* Trama de puntos sutil */}
        <svg className="absolute inset-0 h-full w-full opacity-[0.05]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="wc-dots" x="0" y="0" width="26" height="26" patternUnits="userSpaceOnUse">
              <circle cx="2" cy="2" r="1.4" fill="#111111" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#wc-dots)" />
        </svg>
        {/* Vignette suave hacia el centro para que la card respire */}
        <div
          className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse at center, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 55%)' }}
        />
      </div>

      {/* Wrapper que orbita lento una vez formado el círculo */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: `translateX(${parallax}px)`,
          animation: phase === 'circle' ? 'wcOrbit 180s linear infinite' : undefined,
        }}
      >
        {IMAGES.map((src, i) => {
          let target = { x: 0, y: 0, rotation: 0, scale: 1, opacity: 1 };
          if (phase === 'scatter') {
            target = scatter[i];
          } else if (phase === 'line') {
            const spacing = 110;
            target = { x: i * spacing - (TOTAL * spacing) / 2, y: 0, rotation: 0, scale: 1, opacity: 1 };
          } else {
            const angle = (i / TOTAL) * 360;
            const rad = (angle * Math.PI) / 180;
            target = {
              x: Math.cos(rad) * radius,
              y: Math.sin(rad) * radius,
              rotation: angle + 90,
              scale: 1,
              opacity: 1,
            };
          }
          return <FlipCard key={src} src={src} target={target} />;
        })}
      </div>

      {/* Contenido central (el login) */}
      <div className="relative z-10 flex h-full w-full items-center justify-center p-4">
        {children}
      </div>

      <style>{`
        @keyframes wcFloatA {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(6vw, 4vh) scale(1.15); }
        }
        @keyframes wcFloatB {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-5vw, -5vh) scale(1.1); }
        }
        @keyframes wcOrbit {
          from { transform: translateX(var(--wc-parallax, 0px)) rotate(0deg); }
          to   { transform: translateX(var(--wc-parallax, 0px)) rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
